import { useEffect, useState } from "react";
import { Button, Dialog, Input, toast } from "../ui";
import { governanceApi, errMessage, localInputToIso } from "./api";

const KIND_LABELS = {
  GBM: "General Body Meeting (GBM)",
  OWNERS: "Owners' meeting",
  MEDIATION: "Mediation (Elders' Council)",
};

const TITLE_MIN = 8;

// Who may create which kind (mirrors api/governance.create_meeting):
// GBM → board/admins; OWNERS → board (residents convene from a
// 10%-supported request, not this dialog); MEDIATION → Elders' Council only.
export function meetingKindsFor(me = {}) {
  const kinds = [];
  if (me.is_board) {
    kinds.push("GBM", "OWNERS");
  }
  if (me.is_elder) {
    kinds.push("MEDIATION");
  }
  return kinds;
}

export default function CreateMeetingDialog({
  open,
  onOpenChange,
  onCreated,
  // Restrict the kind picker to these values (defaults to all). When
  // exactly one is given the selector is locked and hidden.
  kinds = ["GBM", "OWNERS", "MEDIATION"],
  initialTitle = "",
}) {
  const locked = kinds.length === 1;
  const [kind, setKind] = useState(kinds[0] || "GBM");
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState("");
  const [venue, setVenue] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setKind(kinds[0] || "GBM");
    setTitle(initialTitle);
    setDescription("");
    setVenue("");
    setScheduledAt("");
  };

  // Apply the requested kind/title each time the dialog is opened — props
  // may arrive after the initial mount (e.g. the issue is still loading).
  useEffect(() => {
    if (open) {
      setKind(kinds[0] || "GBM");
      setTitle(initialTitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const titleShort = title.trim().length > 0 && title.trim().length < TITLE_MIN;
  const valid = title.trim().length >= TITLE_MIN;

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const body = {
        kind,
        title: title.trim(),
        description: description.trim() || undefined,
        venue: venue.trim() || undefined,
        scheduled_at: localInputToIso(scheduledAt) || undefined,
      };
      const meeting = await governanceApi.createMeeting(body);
      toast.success("Meeting drafted — build the agenda, then issue the notice.");
      reset();
      onOpenChange(false);
      if (onCreated) onCreated(meeting);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title="Create meeting"
      className="vsgov-newissue"
      footer={
        <>
          <Button variant="secondary" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={submitting}
            disabled={!valid}
          >
            Create draft
          </Button>
        </>
      }
    >
      <div className="vsgov-field" style={{ marginTop: 0 }}>
        <label className="vsgov-label" htmlFor="vsgov-meeting-kind">
          Kind
        </label>
        {locked ? (
          <input
            id="vsgov-meeting-kind"
            className="vsgov-select"
            value={KIND_LABELS[kind] || kind}
            readOnly
            disabled
          />
        ) : (
          <select
            id="vsgov-meeting-kind"
            className="vsgov-select"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            {kinds.map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k] || k}
              </option>
            ))}
          </select>
        )}
        <p className="vsgov-field-hint">
          The meeting starts as a private draft — nobody is notified until you
          issue the notice.
        </p>
      </div>

      <Input
        label="Title"
        placeholder="e.g. Annual General Body Meeting 2026"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        error={titleShort ? `At least ${TITLE_MIN} characters required` : undefined}
        helper={`Minimum ${TITLE_MIN} characters`}
        maxLength={200}
      />

      <div className="vsgov-field">
        <label className="vsgov-label" htmlFor="vsgov-meeting-desc">
          Purpose / description <span className="vsgov-field-hint">(optional)</span>
        </label>
        <textarea
          id="vsgov-meeting-desc"
          className="vsgov-textarea"
          rows={3}
          placeholder="What is this meeting for?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <Input
        label="Venue"
        placeholder="e.g. Community clubhouse"
        value={venue}
        onChange={(e) => setVenue(e.target.value)}
        helper="Required before a notice can be issued"
      />

      <div className="vsgov-field">
        <label className="vsgov-label" htmlFor="vsgov-meeting-when">
          Date &amp; time
        </label>
        <input
          id="vsgov-meeting-when"
          type="datetime-local"
          className="vsgov-select"
          value={scheduledAt}
          onChange={(e) => setScheduledAt(e.target.value)}
        />
        <p className="vsgov-field-hint">
          Must be in the future before a notice can be issued. You can set it
          later from the draft.
        </p>
      </div>
    </Dialog>
  );
}
